import classNames from 'classnames/bind';
import Box from 'src/components/ui-kit/box/box';
import Table from 'src/components/ui-kit/table/table';
import TableCell from 'src/components/ui-kit/table/_cell';
import TableRow from 'src/components/ui-kit/table/_row';
import React, { DetailedHTMLProps, HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styles from './table.module.scss';
import ListingsTableRow from './_row';
import { useSelector } from 'react-redux';
import TableSpinner from 'src/components/ui-kit/table/_spinner';
import { OrderByDirection } from 'src/enums/order-by-direction';
import ListingsTableHeaderAction from './_header-action';
import { ListingOrderBy } from 'src/enums/listing';
import Link from 'next/link';
import { useRouterListings } from '../../../../hooks/use-router-listings';
import { useRouter } from 'next/router';
import { MineralListingsPageSelectors } from '../../../../store/pages/mineral-listings';

const classesContext = classNames.bind(styles);

export default function ListingsTable({
  className,
  ...restProps
}: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>): JSX.Element {
  const { t } = useTranslation(['components:pages:listings:table']);

  const router = useRouter();
  const routerListings = useRouterListings();

  const currentPageParams = routerListings.getCurrentPageParams();

  const [sort, setSort] = useState<{ orderBy?: ListingOrderBy; orderByDirection?: OrderByDirection }>({
    orderBy: currentPageParams.orderBy,
    orderByDirection: currentPageParams.orderByDirection
  });

  useEffect(() => {
    setSort({
      orderBy: currentPageParams.orderBy,
      orderByDirection: currentPageParams.orderByDirection
    });
  }, [router.query]);

  const listings = useSelector(MineralListingsPageSelectors.items);
  const isLoading = useSelector(MineralListingsPageSelectors.isLoading);

  const columns = [
    { orderBy: ListingOrderBy.ID, label: t('TEXT_LISTING_ID'), class: 'table_cell__id' },
    { orderBy: ListingOrderBy.STARTING_BID, label: t('TEXT_STARTING_BID'), class: 'table_cell__bid' },
    { orderBy: ListingOrderBy.STATE_ID, label: t('TEXT_STATE'), class: 'table_cell__state' },
    { orderBy: ListingOrderBy.COUNTY_ID, label: t('TEXT_COUNTY'), class: 'table_cell__county' },
    { orderBy: ListingOrderBy.NET_ACRES, label: t('TEXT_NET_ACRES'), class: 'table_cell__acres' },
    { orderBy: ListingOrderBy.AVERAGE_MONTHLY_CASH_FLOW, label: t('TEXT_CASH_FLOW'), class: 'table_cell__cash' }
  ];

  const buildOrderByUrl = (orderBy: ListingOrderBy): string => {
    let orderByDirection =
      orderBy === ListingOrderBy.NET_ACRES || orderBy === ListingOrderBy.AVERAGE_MONTHLY_CASH_FLOW
        ? OrderByDirection.DESC
        : OrderByDirection.ASC;

    if (sort.orderBy === orderBy) {
      orderByDirection = sort.orderByDirection === OrderByDirection.ASC ? OrderByDirection.DESC : OrderByDirection.ASC;
    }

    return routerListings.buildPageUrl({
      orderBy,
      orderByDirection
    });
  };

  return (
    <Box
      className={classesContext(['listings', className])}
      {...restProps}>
      <Table className={styles.table}>
        <TableRow
          isHeader
          className={styles.listings_header}>
          {columns.map((column) => (
            <TableCell
              className={classesContext(['table_cell', column.class])}
              key={column.orderBy}>
              <Link
                href={buildOrderByUrl(column.orderBy)}
                passHref
                shallow={false}
                scroll={false}>
                <ListingsTableHeaderAction
                  direction={sort.orderBy === column.orderBy ? sort.orderByDirection : undefined}
                >
                  {column.label}
                </ListingsTableHeaderAction>
              </Link>
            </TableCell>
          ))}
        </TableRow>
        {!isLoading && listings?.map((listing) => <ListingsTableRow
          key={listing.id}
          listing={listing} />)}
        {!isLoading && !listings.length && <h3 className={styles.table_message}>{t('TEXT_NO_RESULT_FOUND')}</h3>}
        {isLoading && <TableSpinner />}
      </Table>
    </Box>
  );
}
